import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {AssetService} from "../asset/asset.service";
import {
  SelectableAsset,
  selectableAssetModelListFactory,
  updateSelectableAssetModelSearchValues,
} from "../types/selectable-asset";
import {createEvent} from "../misc/event.polyfill";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SearchHelpers} from "../helpers/search-helper";
import {GroupByObject} from "../misc/reduce-helpers";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "asset-picker",
  templateUrl: "./asset-selection.template.pug",
  providers: [AssetService],
})
@exposeComponentInDebugMode()
export class AssetSelectionComponent implements OnInit, OnChanges {
  // List of Items to Display
  assets: SelectableAsset[] = [];
  filterAssets: SelectableAsset[] = [];
  displayAssets: SelectableAsset[] = [];

  // Additional features
  @Input() disableAll: boolean = false;
  @Input() disableInstalled: boolean = false;
  @Input() disableLocalizations: boolean = false;
  @Input() hiddenColumns: string[] = [];

  // Parameters for Filtering
  @Input() orderId: string;
  @Input() deploymentId: string;
  @Input() deviceDefectId: string;
  @Input() deviceRelocationId: string;
  @Input() includeRemovals: boolean;

  // Common Properties for Grids
  public currentPage: number = DEFAULT_PAGE;
  private _pageSize: number = DEFAULT_PAGE_LIMIT;
  public maxPages: number;
  public searchText: string = "";
  public error: Error;

  @Output()
  public onAssetSelected = new EventEmitter<any>();

  @Output()
  public onLocalizationCodeChange = new EventEmitter<any>();

  @Output()
  public onChange = new EventEmitter<any>();

  @Input()
  set pageSize(size: any) {
    this._pageSize = +size;
  }

  get pageSize() {
    return this._pageSize;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes["orderId"] && changes["orderId"].currentValue) ||
      (changes["deploymentId"] && changes["deploymentId"].currentValue) ||
      (changes["deviceDefectId"] && changes["deviceDefectId"].currentValue) ||
      (changes["deviceRelocationId"] && changes["deviceRelocationId"].currentValue)
    ) {
      this.loadData();
    } else if (changes["pageSize"] && changes["pageSize"].currentValue !== +this._pageSize) {
      this.loadData();
    }
  }

  ngOnInit(): void {
    this.emitIds();
  }

  private emitIds(): void {
    const event = createEvent("onAssetSelected");

    event["assetIds"] = [];
    event["accessoryIds"] = [];
    event["softwareIds"] = [];

    this.assets.forEach((asset) => {
      if (!asset.selectedNew && asset.localizationCodeId) asset.localizationCodeId = null;

      switch (asset.type) {
        case "asset removal":
          if (asset.selectedNew) {
            event["assetIds"].push(asset.id);
          }
          break;
        case "asset":
          if (asset.selectedNew) {
            event["assetIds"].push(asset.id);
            asset.children.forEach((c) => {
              if (!c.installed) {
                if (c.type === "accessory") event["accessoryIds"].push(c.id);
                else event["softwareIds"].push(c.id);
              }
            });
          }
          break;
        case "accessory":
          if (asset.selectedNew) {
            event["accessoryIds"].push(asset.id);
          }
          break;
        case "software":
          if (asset.selectedNew) {
            event["softwareIds"].push(asset.id);
          }
          break;
        default:
          break;
      }

      this.setLocalizationCode(asset, asset.localizationCodeId);
    });

    this.onAssetSelected.emit(event);
  }

  private updateSelection(): void {
    this.emitIds();
    this.onChange.emit(createEvent("onChange"));
  }

  /**
   * Returns if the Hidden Columns contain all the values passed on the parameter
   * @param valuesArray Array of strings with the values to look for
   * @return {boolean} True if all the values exist on the hidden columns collection
   */
  hiddenColumnsContainsAll(valuesArray = []): boolean {
    return valuesArray.reduce(
      (res, value) => res && this.hiddenColumns.includes(value),
      true,
    );
  }

  setLocalizationCode(asset: SelectableAsset, codeId: string): void {
    asset.localizationCodeId = codeId;
    const assetChanged = {
      id: asset.id,
      type: asset.type,
      localizationCodeId: codeId,
      selected: asset.selectedNew,
    };
    this.onLocalizationCodeChange.emit(assetChanged);
  }

  refresh(): void {
    this.loadData()
      .then(() => this.emitIds());
  }

  recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filterAssets.length / (+this.pageSize));
  }

  updateDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.filterAssets = this.assets.filter((asset) => {
      if (this.searchText) {
        if (!asset.searchValues) return false;

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => asset.searchValues.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayAssets = this.filterAssets.slice(start, end);
    this.recalculateMaxPages();
  }

  @handleErrors()
  async loadData(): Promise<void> {
    if (!this.orderId && !this.deploymentId && !this.deviceDefectId && !this.deviceRelocationId) {
      this.assets = [];
      this.updateDisplay();
      return;
    }

    const {rows} = await this.assetService.getAssetsPicker({
      orderId: this.orderId,
      deploymentId: this.deploymentId,
      deviceDefectId: this.deviceDefectId,
      deviceRelocationId: this.deviceRelocationId,
      includeRemovals: this.includeRemovals ? "true" : "false",
    });

    const selectedIds = {};

    this.assets.forEach((asset) => {
      if (asset.selectedNew) selectedIds[asset.id] = true;

      asset.children.forEach((c) => {
        if (c.selectedNew) selectedIds[c.id] = true;
      });
    });

    rows.forEach((row) => {
      if (row.selected || row.selectedNew) selectedIds[row.id] = true;
    });

    const allAssets = selectableAssetModelListFactory(rows, selectedIds);

    this.assets = [];

    const solutionsIndex = allAssets.filter(it => (it.type === "accessory" || "software") && it.assetId)
      .reduce((obj, item) => obj.add(item.assetId, item), new GroupByObject(true))
      .toObject() as {[key: string]: SelectableAsset[]};

    allAssets.forEach((asset) => {
      switch (asset.type) {
        case "asset removal":
          this.assets.push(asset);
          break;
        case "asset":
          if (solutionsIndex[asset.id]) {
            asset.children = solutionsIndex[asset.id];
            delete solutionsIndex[asset.id];
          }
          this.assets.push(asset);
          break;
        case "accessory":
          if (!asset.assetId) {
            this.assets.push(asset);
          }
          break;
        case "software":
          if (!asset.assetId) {
            this.assets.push(asset);
          }
          break;
        default:
          break;
      }
    });

    Object.keys(solutionsIndex)
      .forEach((assetId) => {
        const accessories = solutionsIndex[assetId];
        this.assets = this.assets.concat(accessories);
      });

    this.assets.forEach((asset) => {
      updateSelectableAssetModelSearchValues(asset);
    });

    this.recalculateMaxPages();

    this.updateDisplay();
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.updateDisplay(); // todo: filter instead
  }

  deselectAll(): void {
    for (const asset of this.assets) {
      if (!this.disableInstalled || !asset.installed) {
        asset.selectedNew = false;
      }

      if (asset.children) {
        for (const item of asset.children) {
          if (!this.disableInstalled || !item.installed) {
            item.selectedNew = false;
          }
        }
      }
    }

    this.updateSelection();
  }

  selectAll(): void {
    for (const asset of this.assets) {
      if (!this.disableInstalled || !asset.installed) {
        asset.selectedNew = true;
      }

      if (asset.children) {
        for (const item of asset.children) {
          if (!this.disableInstalled || !item.installed) {
            item.selectedNew = true;
          }
        }
      }
    }

    this.updateSelection();
  }

  deselectAllVisible(): void {
    for (const asset of this.filterAssets) {
      if (!this.disableInstalled || !asset.installed) {
        asset.selectedNew = false;
      }

      if (asset.children) {
        for (const item of asset.children) {
          if (!this.disableInstalled || !item.installed) {
            item.selectedNew = false;
          }
        }
      }
    }

    this.updateSelection();
  }

  selectAllVisible(): void {
    for (const asset of this.filterAssets) {
      if (!this.disableInstalled || !asset.installed) {
        asset.selectedNew = true;
      }

      if (asset.children) {
        for (const item of asset.children) {
          if (!this.disableInstalled || !item.installed) {
            item.selectedNew = true;
          }
        }
      }
    }

    this.updateSelection();
  }

  constructor(private assetService: AssetService) {}
}
